//import React, { useEffect } from 'react';
import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import StocksType from './StocksType';

// Import Material-UI icons
import ListAltIcon from '@mui/icons-material/ListAlt';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

///const filter = createFilterOptions<StocksType>();

export default function MainHome() {
  const [value, setValue] = React.useState<StocksType | null>(null);
  const [options, setOptions] = React.useState<StocksType[]>([]);
  const [inputValue, setInputValue] = React.useState('');

  // Debounce function
  const debounce = (func: Function, wait: number) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Cached results
  const searchCache = React.useRef<Record<string, StocksType[]>>({});

  // Debounced search function
  const searchStocks = React.useCallback(
    debounce(async (searchTerm: string) => {
      // Don't search for less than 2 characters
      if (searchTerm.length < 2) {
        setOptions([]);
        return;
      }

      // Check cache first
      if (searchCache.current[searchTerm]) {
        setOptions(searchCache.current[searchTerm]);
        return;
      }

      try {
        const response = await fetch(`/stocks/search?q=${encodeURIComponent(searchTerm)}`);
        const data = await response.json();
        // Cache the results
        searchCache.current[searchTerm] = data;
        setOptions(data);
      } catch (error) {
        console.error('Error fetching stocks:', error);
        setOptions([]);
      }
    }, 300), // 300ms delay
    []
  );

  // Add this new function to handle form submission
  const handleFormSubmit = (selectedValue: StocksType | null) => {
    if (selectedValue) {
      window.location.href = `/search?stock=${encodeURIComponent(selectedValue.name)}`;
    }
  };

  const optionsList = [
    { name: 'AI Chat', link: '/chat', icon: ChatIcon, color: '#FF69B4' },
    { name: 'Leaderboard', link: '/leaderboard', icon: ListAltIcon, color: '#4CAF50' },
    { name: 'Projects', link: '/my_projects', icon: FolderOpenIcon, color: '#9C27B0' },
    { name: 'Insights', link: '/insights', icon: LightbulbIcon, color: '#2196F3' },
    { name: 'Analyze Earnings Calls', link: '/earnings_calls', icon: RecordVoiceOverIcon, color: '#FF9800' },
    { name: 'Analyze SEC Filings', link: '/sec_filings', icon: DescriptionIcon, color: '#795548' },
    { name: 'Pricing', link: '/pricing', icon: CreditCardIcon, color: '#607D8B' },
    { name: 'FAQ', link: '/faq', icon: HelpOutlineIcon, color: '#FFC107' },
    { name: 'Stock screener', link: '/screener', icon: SearchIcon, color: '#00BCD4' },
  ];

  return (
    <>
      <style>
        {`.home-container {
            max-width: 800px;
            margin: 0 auto;
            padding: 2rem;
          }
          .search-section {
            text-align: center;
            margin-bottom: 2rem;
          }
          .search-section h1 {
            white-space: nowrap;
            font-size: clamp(1.5rem, 4vw, 2rem); /* Responsive font size */
          }
          .search-form {
            display: flex;
            gap: 0.5rem;
            align-items: center;
          }
          .search-button {
            background-color: #007bff;
            color: white;
            padding: 10px;
          }
          .search-button:hover {
            background-color: #0056b3;
          }
          .options-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
          }
          .option-card {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem;
            background-color: #f8f9fa;
            border-radius: 4px;
            text-decoration: none;
            color: #6c757d; /* Lighter font color */
            transition: background-color 0.3s, color 0.3s;
          }
          .option-card:hover {
            background-color: #e9ecef;
            color: #495057; /* Slightly darker on hover for better contrast */
            text-decoration: none;
          }
          .new-page-icon {
            font-size: 1.2rem;
          }
          .option-icon {
            margin-right: 8px;
          }
          .option-name {
            font-weight: 500; /* Medium font weight for better readability */
          }
          .announcement-container {
            width: 100%;
            display: flex;
            justify-content: center;
            margin-bottom: 2rem;
          }

          .announcement-banner {
            background-color: rgb(219, 242, 241);
            border-radius: 100px;
            padding: 8px 20px;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: transform 0.2s;
            white-space: nowrap;
            font-size: clamp(0.8rem, 3vw, 1rem);
          }

          .announcement-banner:hover {
            transform: translateY(-2px);
          }

          .announcement-tag {
            background-color: rgb(75, 135, 139);
            color: white;
            padding: 4px 12px;
            border-radius: 100px;
            font-size: clamp(0.75rem, 2.5vw, 0.875rem);
            font-weight: 500;
            white-space: nowrap;
          }

          .announcement-text {
            color: rgb(51, 51, 51);
            font-size: clamp(0.8rem, 3vw, 0.9375rem);
            font-weight: 500;
            white-space: nowrap;
          }

          .announcement-link {
            text-decoration: none;
            display: inline-block;
          }

          .announcement-arrow {
            font-size: 18px;
            margin-left: 4px;
          }
        `}
      </style>
      <div className="home-container">
        <div className="announcement-container">
          <a href="/autonomous_ai_agents" className="announcement-link">
            <div className="announcement-banner">
              <span className="announcement-tag">New</span>
              <span className="announcement-text">
                Introducing Autonomous AI Agents
                <span className="announcement-arrow">›</span>
              </span>
            </div>
          </a>
        </div>
        <div className="search-section">
          <h1>Find a stock to analyze</h1>
          <form className="search-form" action="/search" method="get" onSubmit={(e) => {
            e.preventDefault();
            handleFormSubmit(value);
          }}>
            <Autocomplete<StocksType, false, false, true>
              value={value}
              onChange={(event: React.SyntheticEvent, newValue: StocksType | null) => {
                setValue(newValue);
                if (newValue) {
                  handleFormSubmit(newValue);
                }
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
                searchStocks(newInputValue);
              }}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              id="stock-search"
              options={options}
              getOptionLabel={(option: StocksType) => option.name}
              renderOption={(props, option: StocksType) => {
                return (
                  <li {...props}>{option.name}</li>
                );
              }}
              fullWidth
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="stock"
                  label="Enter stock name or ticker"
                  variant="outlined"
                  sx={{ flexGrow: 1 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  autoComplete="off"
                />
              )}
            />
            <IconButton type="submit" className="search-button" aria-label="search">
              <SearchIcon />
            </IconButton>
          </form>
        </div>
        <div className="options-grid">
          {optionsList.map((option) => (
            <a key={option.name} href={option.link} className="option-card">
              <div className="option-content">
                <option.icon className="option-icon" style={{ color: option.color }} />
                <span className="option-name">{option.name}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </>
  );
}

